import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { CreditCard, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MOYASAR_PUBLISHABLE_KEY = process.env.REACT_APP_MOYASAR_PUBLISHABLE_KEY;

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planId = searchParams.get('plan');
  const formRef = useRef(null);
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txId, setTxId] = useState(null);
  const moyasarLoaded = useRef(false);

  useEffect(() => {
    if (!planId) { navigate('/billing'); return; }

    const initPayment = async () => {
      try {
        const res = await api.post('/subscription/checkout', {
          plan_id: planId,
          origin_url: window.location.origin,
        });
        setPlanData(res.data);
        setTxId(res.data.tx_id);
        loadMoyasarForm(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to initialize payment');
        navigate('/billing');
      } finally {
        setLoading(false);
      }
    };

    // Load Moyasar CSS
    if (!document.querySelector('link[href*="moyasar"]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.7/dist/moyasar.css';
      document.head.appendChild(css);
    }

    // Load Moyasar JS
    if (!window.Moyasar && !document.querySelector('script[src*="moyasar"]')) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.7/dist/moyasar.umd.min.js';
      script.onload = () => initPayment();
      document.head.appendChild(script);
    } else {
      initPayment();
    }
  }, [planId, navigate]);

  const loadMoyasarForm = (data) => {
    if (moyasarLoaded.current || !window.Moyasar) return;
    moyasarLoaded.current = true;

    setTimeout(() => {
      if (!document.querySelector('.moyasar-form')) return;

      window.Moyasar.init({
        element: '.moyasar-form',
        amount: data.amount,
        currency: data.currency,
        description: data.description,
        publishable_api_key: MOYASAR_PUBLISHABLE_KEY,
        callback_url: data.callback_url,
        supported_networks: ['visa', 'mastercard', 'mada'],
        methods: ['creditcard'],
        on_completed: async function (payment) {
          try {
            await api.post('/subscription/verify-payment', {
              payment_id: payment.id,
              tx_id: data.tx_id,
              plan_id: data.plan_id,
            });
          } catch (err) {
            console.error('Payment save error:', err);
          }
        },
      });
    }, 200);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-2 border-black border-t-[#FF5A00] animate-spin" />
          <p className="mt-4 font-mono text-sm text-[#5E5E5E]">Preparing payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-xl mx-auto" data-testid="payment-page">
      <button
        onClick={() => navigate('/billing')}
        data-testid="back-to-billing"
        className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-wide text-[#5E5E5E] hover:text-[#FF5A00] mb-6 transition-colors"
      >
        <ArrowLeft size={14} weight="bold" /> Back to Billing
      </button>

      <div className="bg-white border border-black/15">
        {/* Plan Summary */}
        <div className="p-6 border-b border-black/15">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FF5A00] flex items-center justify-center">
              <CreditCard size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <p className="font-sans text-lg font-bold tracking-tight">Subscribe to {planData?.plan_name}</p>
              <p className="font-mono text-xs text-[#5E5E5E]">Monthly subscription</p>
            </div>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="font-sans text-3xl font-black">{(planData?.amount / 100).toFixed(0)}</span>
            <span className="font-mono text-sm text-[#8F8F8F]">SAR / month</span>
          </div>
        </div>

        {/* Moyasar Form */}
        <div className="p-6">
          <div className="moyasar-form" ref={formRef}></div>
        </div>

        {/* Security Note */}
        <div className="px-6 py-3 border-t border-black/10 bg-[#F7F7F7] flex items-center gap-2">
          <ShieldCheck size={16} weight="bold" className="text-[#00A859]" />
          <p className="font-mono text-[10px] text-[#8F8F8F]">
            Secured by Moyasar. Supports mada, Visa, Mastercard.
          </p>
        </div>
      </div>
    </div>
  );
}
